// Afrekeningen per maand — de cijfers achter de grafiek op de Bill Shock-pagina.
//
// Een bill-shock-berekening loopt over een periode "van → tot". Het einde van die
// periode is het moment waarop de klant zijn afrekening krijgt: dan wordt het
// verschil écht betaald of terugbetaald. Gegroepeerd op die eindmaand zie je dus
// hoeveel er in een bepaalde maand samen nog bij te betalen is (positief) en
// hoeveel er terug moet (negatief) — de planning achter de lijst.
//
// Twee niveaus, dezelfde als de weergave van de tabel:
//   'record' — elke berekening telt apart mee in haar richting.
//   'klant'  — eerst het NETTO per klant binnen die maand, dan pas de richting.
//              Een klant met + € 800 op één EAN en − € 300 op een andere, allebei
//              met een afrekening in november, betaalt in november € 500 bij; hem
//              aan beide kanten tellen zou de maand te zwaar maken.
//
// De functie krijgt de GEFILTERDE rijen: wat de filters wegnemen, telt hier niet.
// Maanden tussen de eerste en de laatste afrekening zonder berekeningen staan er
// wél in (op nul), anders lijkt een lege maand op een maand die niet bestaat.
// Een berekening zonder einddatum is niet te plaatsen; ze wordt geteld in
// `zonderDatum`, zodat het scherm kan zeggen dat ze ontbreekt.

import { maandVanDatum } from './billshockPeriode';
import { klantSleutel } from './billshockSort';

const getal = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

// 'JJJJ-MM' → de volgende maand.
const volgendeMaand = (m) => {
  const [j, mm] = m.split('-').map(Number);
  return mm === 12 ? `${j + 1}-01` : `${j}-${String(mm + 1).padStart(2, '0')}`;
};

// Korte Belgische maandnaam voor de as: "nov 2026".
export const maandLabel = (m) => {
  const [j, mm] = String(m).split('-').map(Number);
  if (!j || !mm) return String(m);
  return new Intl.DateTimeFormat('nl-BE', { month: 'short', year: 'numeric' }).format(new Date(Date.UTC(j, mm - 1, 15)));
};

export function perAfrekeningsmaand(rijen, { niveau = 'record', klantNaam = () => '' } = {}) {
  const perMaand = new Map();     // maand → lijst van bedragen (per record of per klant)
  let zonderDatum = 0;
  if (niveau === 'klant') {
    // Netto per (maand, klant), daarna pas splitsen op richting.
    const perMaandKlant = new Map();
    for (const r of rijen) {
      const m = maandVanDatum(r.toUtc);
      if (!m) { zonderDatum++; continue; }
      const k = `${m}|${klantSleutel(r, klantNaam)}`;
      perMaandKlant.set(k, (perMaandKlant.get(k) || 0) + getal(r.billShockAmount));
    }
    for (const [k, netto] of perMaandKlant) {
      const m = k.slice(0, 7);
      if (!perMaand.has(m)) perMaand.set(m, []);
      perMaand.get(m).push(netto);
    }
  } else {
    for (const r of rijen) {
      const m = maandVanDatum(r.toUtc);
      if (!m) { zonderDatum++; continue; }
      if (!perMaand.has(m)) perMaand.set(m, []);
      perMaand.get(m).push(getal(r.billShockAmount));
    }
  }

  const maanden = [...perMaand.keys()].sort();
  const reeks = [];
  if (maanden.length) {
    const laatste = maanden[maanden.length - 1];
    for (let m = maanden[0]; m <= laatste; m = volgendeMaand(m)) {
      const bedragen = perMaand.get(m) || [];
      let betalen = 0, ontvangen = 0, aantalBetalen = 0, aantalOntvangen = 0;
      for (const b of bedragen) {
        if (b > 0) { betalen += b; aantalBetalen++; }
        else if (b < 0) { ontvangen += b; aantalOntvangen++; }
      }
      reeks.push({
        maand: m,
        label: maandLabel(m),
        betalen,                 // som van wat klanten bijbetalen (≥ 0)
        ontvangen,               // som van wat klanten terugkrijgen (≤ 0, voor de staaf onder de nullijn)
        netto: betalen + ontvangen,
        aantalBetalen,
        aantalOntvangen,
      });
    }
  }
  return { reeks, zonderDatum };
}

// Cumulatief tot en met elke maand: het volledige bill-shock-bedrag van alle
// klanten die dan al hun afrekening kregen, opgeteld bij de maanden ervoor.
// Zelfde vorm als de reeks van perAfrekeningsmaand, zodat dezelfde kaart het
// kan tekenen: `betalen` is de som van alle positieve maanden tot nu, `ontvangen`
// die van alle negatieve, de aantallen tellen op dezelfde manier door. De
// laatste maand is dus het totaal van de hele selectie.
export function cumulatief(reeks) {
  let betalen = 0, ontvangen = 0, aantalBetalen = 0, aantalOntvangen = 0;
  return reeks.map(m => {
    betalen += m.betalen; ontvangen += m.ontvangen;
    aantalBetalen += m.aantalBetalen; aantalOntvangen += m.aantalOntvangen;
    return { maand: m.maand, label: m.label, betalen, ontvangen, netto: betalen + ontvangen, aantalBetalen, aantalOntvangen };
  });
}
